/**
 * =============================================================================
 * useWlRecentContracts
 *
 * Hook do painel WL → Financeiro. Lista contratações recentes (planos e
 * add-ons) cross-tenant, aplicando a estrutura híbrida legado + Planos 2.0
 * já implementada nas demais RPCs de faturamento.
 *
 * Fonte: RPC `wl_list_recent_contracts` v1.0.0.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial (substitui useWlRecentCharges).
 * =============================================================================
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** Tipo de contratação exibido no card. */
export type WlContractType = 'plan' | 'addon';

/** Linha retornada pela RPC. */
export interface WlRecentContractItem {
  id: string;
  contract_type: WlContractType;
  contracted_at: string;
  tenant_id: string;
  tenant_name: string;
  tenant_status: string;
  is_courtesy: boolean;
  billing_integration: 'legado' | 'integracao_v1' | string;
  item_name: string;
  recurrence_interval: string | null;
  quantity: number;
  unit_price: number;
  mrr_impact: number;
  currency: string;
  origin_status: string;
  total_count: number;
}

interface Params {
  page: number;                     // 1-indexed
  pageSize: number;
  type?: WlContractType | null;     // filtro plano/add-on
  statuses?: string[] | null;       // filtro global de status do dashboard
  includeCourtesy?: boolean;
}

/** Hook — contratações recentes cross-tenant. */
export function useWlRecentContracts({
  page,
  pageSize,
  type = null,
  statuses = null,
  includeCourtesy = true,
}: Params) {
  return useQuery({
    queryKey: ['wl-recent-contracts', page, pageSize, type, statuses, includeCourtesy],
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<{ items: WlRecentContractItem[]; total: number }> => {
      const { data, error } = await supabase.rpc('wl_list_recent_contracts' as never, {
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
        p_type: type,
        p_statuses: statuses && statuses.length ? statuses : null,
        p_include_courtesy: includeCourtesy,
      } as never);

      if (error) throw error;

      const rows = (data as any[] | null) ?? [];
      const total = rows[0]?.total_count ? Number(rows[0].total_count) : 0;

      const items: WlRecentContractItem[] = rows.map((r) => ({
        id: r.id,
        contract_type: r.contract_type,
        contracted_at: r.contracted_at,
        tenant_id: r.tenant_id,
        tenant_name: r.tenant_name,
        tenant_status: r.tenant_status,
        is_courtesy: !!r.is_courtesy,
        billing_integration: r.billing_integration,
        item_name: r.item_name,
        recurrence_interval: r.recurrence_interval,
        quantity: Number(r.quantity ?? 1),
        unit_price: Number(r.unit_price ?? 0),
        mrr_impact: Number(r.mrr_impact ?? 0),
        currency: r.currency ?? 'BRL',
        origin_status: r.origin_status ?? '',
        total_count: Number(r.total_count ?? 0),
      }));

      return { items, total };
    },
  });
}
